import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plans } from "./components/Plans";
import { RootState } from "../../store/index.ts";
import { HousingsActionCreator } from "../../store/housingsActions.ts";
import { connect } from "react-redux";
import {
  selectHousingsList,
  selectHousingsPending,
  selectCommerceHousingsList,
  selectCommerceHousingsPending,
  selectIsAuth,
  selectUserData,
} from "../../store/selectors.ts";
import { HousingInfo } from "./components/HousingInfo";
import { HousingComplexSkeleton } from "./components/HousingComplexSkeleton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getHousingById } from "./utils";
import { Error } from "../../components/common/Error";
import { PageNavigation } from "../../components/common/PageNavigation";
import { Pricing } from "../../components/common/Pricing";
import { isShared } from "../../utils";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import { PaidHoc } from "../../components/hocs/PaidHoc";
import {
  filterHousingsByClient,
} from "../../integrations/clientConfig";
import { useBranding } from "../../integrations/branding";
import {
  ANALYTICS_EVENTS,
  getCalculationAnalyticsProperties,
  trackAnalyticsEvent,
} from "../../analytics";

type HousingComplexType = "residential" | "commerce";

type Props = {
  basePath?: string;
  type: HousingComplexType;
  housings: any[] | null;
  pending: boolean;
  commerceHousings: any[] | null;
  commercePending: boolean;
  userData: any;
  isAuth: boolean;
  fetchHousings: () => void;
  fetchCommerceHousings: () => void;
};

export const _HousingComplex: React.FC<Props> = ({
  basePath = "",
  type,
  housings,
  pending,
  commerceHousings,
  commercePending,
  userData,
  isAuth,
  fetchHousings,
  fetchCommerceHousings,
}) => {
  const { config, isShowcase } = useBranding();
  const [shareData, setShareData] = useState<any>();
  const lastCalculationFingerprintRef = useRef<string>();
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTargetFromState = (location.state as { housingsReturnTo?: unknown } | null)?.housingsReturnTo;
  const housingsReturnTo =
    typeof returnTargetFromState === "string" && returnTargetFromState.startsWith("/")
      ? returnTargetFromState
      : basePath || "/housings";
  const sourceList = type === "commerce" ? commerceHousings : housings;
  const currentList = useMemo(
    () => filterHousingsByClient(sourceList, type, config),
    [config, sourceList, type],
  );
  const currentPending = type === "commerce" ? commercePending : pending;

  const housing = getHousingById(currentList, routeId);
  const canShowProtectedContent = isShowcase || isShared || Boolean(userData?.user);
  const renderHousingInfo = canShowProtectedContent;
  const hasConfiguredSidebar =
    config.housingDetails.showNavigationSection ||
    config.housingDetails.showFactsSection;

  // Фетчим нужный список по типу
  useEffect(() => {
    if (!currentList) {
      if (type === "commerce") fetchCommerceHousings();
      else fetchHousings();
    }
  }, [currentList, fetchCommerceHousings, fetchHousings, type]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentList]);

  useEffect(() => {
    if (!shareData?.fullPrice) {
      return;
    }

    const properties = getCalculationAnalyticsProperties(
      { ...shareData, propertyType: type },
      housing,
    );
    const fingerprint = JSON.stringify(properties);
    if (lastCalculationFingerprintRef.current === fingerprint) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      lastCalculationFingerprintRef.current = fingerprint;
      trackAnalyticsEvent(ANALYTICS_EVENTS.CALCULATION_READY, properties);
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [housing, shareData, type]);

  if (currentPending && !currentList) {
    return (
      <HousingComplexSkeleton
        showSidebar={hasConfiguredSidebar}
        backLabel={config.housingDetails.backLabel}
        backTo={housingsReturnTo}
      />
    );
  }

  if (!housing?.housingName) {
    return (
      <>
        <div className="mx-auto w-full max-w-[1440px] px-4 pt-4 sm:px-5 lg:px-8">
          <PageNavigation
            backLabel={config.housingDetails.backLabel}
            backTo={housingsReturnTo}
          />
        </div>
        <Error
          title={config.housingDetails.unavailableTitle}
          description="Проверьте правильность ссылки"
        />
      </>
    );
  }

  const isChessPlans = housing?.chessPlans?.length > 0;
  const isLayouts = housing?.layouts?.length > 0;
  const showMaterialsSection =
    config.housingDetails.showMaterialsSection &&
    (isChessPlans || isLayouts);
  const hasSidebar = hasConfiguredSidebar;
  const facts = [
    housing?.address
      ? {
          label: config.housingDetails.factLabels.address,
          value: housing.address,
        }
      : null,
    housing?.deadline
      ? {
          label: config.housingDetails.factLabels.deadline,
          value: housing.deadline,
        }
      : null,
    (isShowcase || isAuth) &&
    config.housingDetails.showBuilder !== false &&
    housing?.builder
      ? {
          label: config.housingDetails.factLabels.builder,
          value: housing.builder,
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto flex w-full max-w-[1440px] flex-col px-4 pb-12 pt-4 sm:px-5 lg:px-8 lg:pb-16">
      <PageNavigation
        backLabel={config.housingDetails.backLabel}
        backTo={housingsReturnTo}
        className="mb-5"
      />

      <div className="grid w-full gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(380px,0.8fr)] xl:items-start">
        <div className="min-w-0 space-y-6 xl:col-span-2">
          {renderHousingInfo ? (
            <>
              <HousingInfo
                housing={housing}
                isAuth={isShowcase || isAuth}
                content={config.housingDetails}
              />
              <div
                className={`grid gap-6 ${hasSidebar ? "xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start" : ""}`.trim()}
              >
                <div className={`min-w-0 space-y-6 ${hasSidebar ? "" : "mx-auto w-full max-w-[1120px]"}`.trim()}>
                  <section
                    id="pricing"
                    className="px-0 py-2 sm:rounded-[28px] sm:border sm:p-6"
                    style={{
                      borderColor: "var(--brand-border, #e7ebf0)",
                      background: "var(--brand-surface, #ffffff)",
                    }}
                  >
                    {isShowcase ? (
                      <Pricing
                        type={type}
                        housing={housing}
                        shareData={shareData}
                        setShareData={setShareData}
                        showPriceList={config.pricing.showPriceList}
                        showCalculator={config.pricing.showCalculator}
                        calculatorEyebrow={config.pricing.eyebrow}
                        calculatorTitle={config.pricing.title}
                        mobileOpenLabel={config.pricing.mobileOpenLabel}
                        mobileCloseLabel={config.pricing.mobileCloseLabel}
                      />
                    ) : (
                      <div>
                        <PaidHoc
                          customTitle="Нет доступа к ценам"
                          customDescription="Для доступа к ценам и калькулятору рассрочки оформите подписку"
                          analyticsFeature="installment_calculator"
                        >
                          <Pricing
                            type={type}
                            housing={housing}
                            shareData={shareData}
                            setShareData={setShareData}
                            showPriceList={config.pricing.showPriceList}
                            showCalculator={config.pricing.showCalculator}
                            calculatorEyebrow={config.pricing.eyebrow}
                            calculatorTitle={config.pricing.title}
                            mobileOpenLabel={config.pricing.mobileOpenLabel}
                            mobileCloseLabel={config.pricing.mobileCloseLabel}
                          />
                        </PaidHoc>
                      </div>
                    )}
                  </section>

                  {showMaterialsSection && (
                    <>
                      {isShowcase ? (
                        <section
                          id="materials"
                          className="px-0 py-2 sm:rounded-[28px] sm:border sm:p-6"
                          style={{
                            borderColor: "var(--brand-border, #e7ebf0)",
                            background: "var(--brand-surface, #ffffff)",
                          }}
                        >
                          <div
                            className="flex flex-col gap-3 pb-4 sm:border-b sm:pb-5 sm:flex-row sm:items-end sm:justify-between"
                            style={{ borderColor: "var(--brand-border, #edf1f5)" }}
                          >
                            <div>
                              <div
                                className="text-[12px] font-semibold uppercase tracking-[0.14em]"
                                style={{ color: "var(--brand-text-secondary, #7a8699)" }}
                              >
                                {config.housingDetails.materialsSectionEyebrow}
                              </div>
                              <h2
                                className="mt-2 text-[28px] font-semibold leading-[0.98] sm:text-[36px]"
                                style={{ color: "var(--brand-text-primary, #101828)" }}
                              >
                                {config.housingDetails.materialsSectionTitle}
                              </h2>
                            </div>
                          </div>

                          <div className="mt-5 space-y-4">
                            {isChessPlans && (
                              <Plans
                                title="Шахматки"
                                chessPlans={housing.chessPlans}
                                defaultName="Шахматка"
                              />
                            )}
                            {isLayouts && (
                              <Plans
                                title="Планировки"
                                chessPlans={housing.layouts}
                                defaultName="Общий план"
                              />
                            )}
                          </div>
                        </section>
                      ) : (
                        <PaidHoc
                          customTitle="Шахматки и планировки"
                          customDescription="Для доступа к шахматкам и планировкам оформите подписку"
                        >
                          <section
                            id="materials"
                            className="px-0 py-2 sm:rounded-[28px] sm:border sm:p-6"
                            style={{
                              borderColor: "var(--brand-border, #e7ebf0)",
                              background: "var(--brand-surface, #ffffff)",
                            }}
                          >
                            <div
                              className="flex flex-col gap-3 pb-4 sm:border-b sm:pb-5 sm:flex-row sm:items-end sm:justify-between"
                              style={{ borderColor: "var(--brand-border, #edf1f5)" }}
                            >
                              <div>
                                <div
                                  className="text-[12px] font-semibold uppercase tracking-[0.14em]"
                                  style={{ color: "var(--brand-text-secondary, #7a8699)" }}
                                >
                                  {config.housingDetails.materialsSectionEyebrow}
                                </div>
                                <h2
                                  className="mt-2 text-[28px] font-semibold leading-[0.98] sm:text-[36px]"
                                  style={{ color: "var(--brand-text-primary, #101828)" }}
                                >
                                  {config.housingDetails.materialsSectionTitle}
                                </h2>
                              </div>
                            </div>

                            <div className="mt-5 space-y-4">
                              {isChessPlans && (
                                <Plans
                                  title="Шахматки"
                                  chessPlans={housing.chessPlans}
                                  defaultName="Шахматка"
                                />
                              )}
                              {isLayouts && (
                                <Plans
                                  title="Планировки"
                                  chessPlans={housing.layouts}
                                  defaultName="Общий план"
                                />
                              )}
                            </div>
                          </section>
                        </PaidHoc>
                      )}
                    </>
                  )}
                </div>

                {hasSidebar && (
                  <aside className="hidden space-y-4 xl:sticky xl:top-24 xl:block">
                    {config.housingDetails.showNavigationSection && (
                      <section
                        className="rounded-[24px] border bg-white p-5"
                        style={{
                          borderColor: "var(--brand-border, #e7ebf0)",
                          background: "var(--brand-surface, #ffffff)",
                        }}
                      >
                        <div
                          className="text-[12px] font-semibold uppercase tracking-[0.12em]"
                          style={{ color: "var(--brand-text-secondary, #7a8699)" }}
                        >
                          {config.housingDetails.navigationTitle}
                        </div>
                        <h3
                          className="mt-2 text-[26px] font-semibold leading-[1]"
                          style={{ color: "var(--brand-text-primary, #101828)" }}
                        >
                          {config.housingDetails.navigationHeading}
                        </h3>
                        <div className="mt-5 grid gap-3">
                          <a
                            href="#pricing"
                            className="rounded-[18px] border border-[#101828] bg-[#101828] px-4 py-3 text-center text-[14px] font-semibold text-white no-underline transition-colors hover:bg-[#182230]"
                            style={{
                              borderColor: "var(--brand-accent, #101828)",
                              background: "var(--brand-accent, #101828)",
                              color: "var(--brand-accent-contrast, #ffffff)",
                            }}
                          >
                            {config.housingDetails.pricingAnchorLabel}
                          </a>
                          {showMaterialsSection && (
                            <a
                              href="#materials"
                              className="rounded-[18px] border px-4 py-3 text-center text-[14px] font-semibold no-underline transition-colors"
                              style={{
                                borderColor:
                                  "var(--brand-secondary-button-border, var(--brand-border, #d0d5dd))",
                                background:
                                  "var(--brand-secondary-button-background, var(--brand-surface, #ffffff))",
                                color:
                                  "var(--brand-secondary-button-text, var(--brand-text-primary, #344054))",
                              }}
                            >
                              {config.housingDetails.materialsAnchorLabel}
                            </a>
                          )}
                        </div>
                      </section>
                    )}

                    {config.housingDetails.showFactsSection && (
                      <section
                        className="rounded-[24px] border bg-white p-5"
                        style={{
                          borderColor: "var(--brand-border, #e7ebf0)",
                          background: "var(--brand-surface, #ffffff)",
                        }}
                      >
                        <div
                          className="text-[12px] font-semibold uppercase tracking-[0.12em]"
                          style={{ color: "var(--brand-text-secondary, #7a8699)" }}
                        >
                          {config.housingDetails.factsTitle}
                        </div>
                        <div className="mt-4 space-y-3">
                          {facts.length > 0 ? (
                            facts.map((item) => (
                              <div
                                key={`${item.label}-${item.value}`}
                                className="border-t px-0 py-3 first:border-t-0 first:pt-0 last:pb-0"
                                style={{ borderColor: "var(--brand-border, #edf1f5)" }}
                              >
                                <div
                                  className="text-[11px] font-semibold uppercase tracking-[0.08em]"
                                  style={{ color: "var(--brand-text-secondary, #98a2b3)" }}
                                >
                                  {item.label}
                                </div>
                                <div
                                  className="mt-2 text-[14px] leading-6"
                                  style={{ color: "var(--brand-text-primary, #1f2937)" }}
                                >
                                  {item.value}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div
                              className="rounded-[18px] border border-dashed px-4 py-3 text-[14px] leading-6"
                              style={{
                                borderColor: "var(--brand-border, #d0d5dd)",
                                background:
                                  "var(--brand-surface-elevated, rgba(255,255,255,0.7))",
                                color: "var(--brand-text-secondary, #667085)",
                              }}
                            >
                              {config.housingDetails.factsEmptyLabel}
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </aside>
                )}
              </div>
            </>
          ) : (
            <>
              <HousingInfo
                housing={housing}
                limitedAccess
                content={config.housingDetails}
              />
              {!isShowcase && (
                <div className="rounded-[28px] border border-[#ead8d8] bg-[linear-gradient(180deg,#fffdfd_0%,#fff4f4_100%)] p-5 shadow-[0_24px_60px_rgba(127,29,29,0.08)] sm:p-6">
                  <Error
                    title={config.housingDetails.accessDeniedTitle}
                    description={config.housingDetails.accessDeniedDescription}
                    buttonText="войти"
                    retryCallback={() => navigate("/signin")}
                    icon={<LockIcon />}
                  />
                </div>
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
};

const mapStateToProps = (
  state: RootState,
  ownProps: { type: HousingComplexType },
) => ({
  housings: selectHousingsList(state),
  pending: selectHousingsPending(state),
  commerceHousings: selectCommerceHousingsList(state),
  commercePending: selectCommerceHousingsPending(state),
  userData: selectUserData(state),
  isAuth: selectIsAuth(state),
});

const mapDispatchToProps = {
  fetchHousings: HousingsActionCreator.fetchHousings,
  fetchCommerceHousings: HousingsActionCreator.fetchCommerceHousings,
};

export const HousingComplex = connect(
  mapStateToProps,
  mapDispatchToProps,
)(_HousingComplex);
